import { Button, Input, Select, Switch, Tooltip } from 'antd';
import { Plus } from 'lucide-react';
import { RANK_GROUPS, type RankGroupKey } from '@pams/shared';
import { ReorderableList } from '@/components/ReorderableList';
import { withChosenOptions } from '@/lib/selectOptions';
import { useRankOptions } from '@/hooks/useRankOptions';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  COMMAND ROLES — an add-row list, defined PER POLICY.
 *
 *  There is no CommandRole master: a Durga Puja or Republic Day policy may declare an
 *  entirely different set. The mitigation for that freedom is "copy from an existing
 *  policy" on the page above — freedom to diverge, with sameness as the easy path.
 *
 *  ⚠️ `key` IS MINTED ONCE AND THEN CARRIED, NEVER RE-DERIVED FROM THE LABEL.
 *
 *  A row that already has a key keeps it through every label edit, and the key travels
 *  on the wire so the server can tell "rename this role" from "add a new one". A new row
 *  has no key yet; the slug shown beside its label is what it WILL get, computed with
 *  the same shared `toSlug` the server will use, so nobody has to guess.
 *
 *  ⚠️ AND THESE ARE NOT COLUMNS. They have no count, no allocation and no shortfall —
 *  FR-5.2.6b. The preview renders them outside the grid for exactly that reason.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type CommandRoleRow = {
  key?: string;
  label: string;
  isRequired: boolean;
  /**
   * ⚠️ [P4-UX.2] EMPTY MEANS ANYONE. It is an allow-list, and the absence of a rule is
   * the default — a policy that says nothing about rank is not a policy that permits
   * nobody.
   */
  eligibleRankIds: string[];
  /**
   * ⚠️ [C2b §5c] GROUP REFERENCES, BESIDE THE IDS AND NEVER MERGED INTO THEM.
   *
   * `{group: AC}` means any Assistant Commissioner including one added next year; five stored
   * ids mean those five, for ever. The picker joins the pair for one control and splits it
   * again on change — the prefix never leaves this file.
   */
  eligibleRankGroupKeys: RankGroupKey[];
};

/** Stable list identity for a row that has no server key yet. */
const rowId = (row: CommandRoleRow, index: number): string => row.key ?? `new-${String(index)}`;

/**
 * ⚠️ [UX1 §6] THIS PAGE HAS NO `copy.ts`, AND ITS SIBLINGS INLINE THEIRS — `SourceCell`,
 * `PlacePicker` and `AssociatedUnitsPicker` all declare a `const … = {…} as const` at the top of
 * the component file. Following that rather than minting a module for two strings.
 */
const COMMAND_RANK_COPY = {
  loading: 'Loading ranks…',
  unavailable: 'No ranks are marked command-eligible yet. Ask your administrator.',
  /* ⚠️ [C2b §5d] The two sections of ONE list — never a mode toggle. See `RANK_OPTION_GROUPS`. */
  groupSection: 'Rank groups',
  rankSection: 'Individual ranks',
  members: (count: number): string => `${String(count)} ranks`,
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b §5c/§5d/§5e] ONE `Select` CARRYING TWO KINDS OF THING.
 *
 *  A command role's allowed ranks is a MIXED list — group references AND individual codes — and
 *  §5d asks for ONE list rather than a mode toggle: a toggle adds a decision before every
 *  selection and SPLITS SEARCH, so typing "insp" would surface the Inspector group or the
 *  Inspectress rank but never both.
 *
 *  ⚠️ THE PREFIX IS A UI ENCODING AND IS NEVER STORED. antd's multi-select has one `value`
 *  array, so a group option is carried as `group:<KEY>` and `onChange` splits it straight back
 *  into the two named columns the wire declares. It cannot collide with a rank: those values
 *  are 24-hex ObjectIds. This is NOT the identity-from-a-mint-format family §7 has corrected
 *  four times — nothing reads the prefix off a STORED value, because no stored value has one.
 *
 *  ⚠️ AND THE TWO KINDS MUST NOT LOOK ALIKE (§5e). Five AC chips mean something different from
 *  one AC group chip, and a user who cannot see the difference picks whichever is first. The
 *  distinction is carried in TEXT and in a `data-kind` attribute, never in colour alone —
 *  jsdom resolves no cascade, so a colour-only difference would be unassertable.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const GROUP_VALUE_PREFIX = 'group:';

const groupValueOf = (key: RankGroupKey): string => `${GROUP_VALUE_PREFIX}${key}`;

const isGroupValue = (value: string): boolean => value.startsWith(GROUP_VALUE_PREFIX);

const groupKeyOfValue = (value: string): string => value.slice(GROUP_VALUE_PREFIX.length);

export const CommandRoleField = ({
  value,
  onChange,
  disabled,
}: {
  value?: CommandRoleRow[];
  onChange?: (value: CommandRoleRow[]) => void;
  disabled?: boolean;
}) => {
  const rows = value ?? [];
  const { commandOptions: commandRankOptions, resolve: resolveRank, isFetching: ranksFetching } = useRankOptions();

  const update = (index: number, patch: Partial<CommandRoleRow>) =>
    onChange?.(rows.map((row, position) => (position === index ? { ...row, ...patch } : row)));

  const items = rows.map((row, index) => {
    const id = rowId(row, index);
    return {
      id,
      label: row.label || `Command role ${String(index + 1)}`,
      content:
        (
          /*
           * ─────────────────────────────────────────────────────────────────────────
           *  ⚠️ [ASSOC-TILE.2 / reported] TWO LINES, BECAUSE THE LABEL WAS THE ONLY
           *  SHRINKABLE ITEM AND ABSORBED THE WHOLE DEFICIT.
           *
           *  This was one flex ROW of three controls. The rank `Select` carried
           *  `w-72 shrink-0` and the required cluster `shrink-0`, so the label `Input` —
           *  which had no width and no `flex` at all — was the only thing that could give
           *  way. `ReorderableList`'s `'row'` layout already spends ~210px of the strip on
           *  the handle, the position badge and the four controls, so below roughly 1100px
           *  of content the name collapsed to two or three characters: an operator saw
           *  `Su`, `As`, `In-` and could not read what they had typed.
           *
           *  ⚠️ IT IS A STACK, NOT A WIDTH BRANCH. `web/vitest.setup.ts` stubs `matchMedia`
           *  to `matches: false` for ever, so a width-BRANCHING render takes the narrow path
           *  in every unit test; and a container query would need its own NAMED `@container`
           *  here, because an unnamed one resolves to `<main>` while this field sits inside
           *  `TwoColumnLayout variant="form"` and has far less ([D4.2]'s rule). Two lines at
           *  every width is one shape, needs neither, and is what was asked for.
           *
           *  ⚠️ AND `required` RIDES WITH THE NAME RATHER THAN WITH THE RANKS. Both are
           *  properties of the ROLE and read together; the rank picker is the control that
           *  genuinely needs measure, because it holds several chips and `maxTagCount`
           *  collapses them the moment it runs out — which is what `w-72` was forcing at
           *  288px on every screen, wide ones included.
           * ─────────────────────────────────────────────────────────────────────────
           */
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {/*
            ⚠️ [amended C1f §8d] THE SLUG CHIP IS GONE AND ITS SENTENCE IS HERE.

            It rendered `row.key ?? toSlug(row.label)` in a mono badge — a raw `assisting_ac`
            beside a human name, on the one screen an operator writes these on. §8d says the
            key must not be RENDERED; it STAYS IN THE MODEL and on the wire, because [P2] made
            it the stable identity and a label edit is a rename rather than a new role.

            The information the chip carried is the reassurance below, which needs no slug to
            say. The invariant it stood for — a key survives a rename — is asserted on the
            SUBMITTED PAYLOAD in `PolicyBuilder.test.tsx`, which is a stronger test of the
            actual rule than a rendered badge ever was.
          */}
              <Tooltip title="This role keeps its identity when you rename it.">
                <Input
                  size="small"
                  className="min-w-0 flex-1"
                  value={row.label}
                  disabled={disabled}
                  placeholder="Incharge"
                  aria-label={`Command role ${String(index + 1)} label`}
                  data-testid={`command-role-label-${String(index)}`}
                  onChange={(event) => update(index, { label: event.target.value })}
                />
              </Tooltip>
              {/*
            ⚠️ `flex-1 min-w-0` IS THE HALF THAT ACTUALLY FIXES IT. Stacking gives the name a
            line; without these it would STILL be the only shrinkable item on that line and
            would still give way to the switch beside it. `min-w-0` is what lets a flex item
            go below its intrinsic content width at all.
          */}
              <Tooltip title="A hint for the standard intake form. It never blocks an arrangement.">
                <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <Switch
                    size="small"
                    checked={row.isRequired}
                    disabled={disabled}
                    aria-label={`${row.label || `Command role ${String(index + 1)}`} is required on the intake form`}
                    onChange={(checked) => update(index, { isRequired: checked })}
                  />
                  required
                </span>
              </Tooltip>
            </div>
            {/*
            ⚠️ [P4-UX.2] OPTIONAL BY CONSTRUCTION, AND THE PLACEHOLDER SAYS SO.

            "In Charge may only be an Inspector or above" is a real rule in some policies
            and no rule at all in others, so this is a per-role allow-list rather than a
            new master. Leaving it empty is the common case and means any rank may be
            picked — which is why the placeholder reads "Any rank" rather than sitting
            blank and looking unfinished.

            It filters who may be PICKED. It counts nothing, allocates nothing and blocks
            no publish — FR-5.2.6b is untouched.
          */}
            <Tooltip title="Optional. Leave empty and anyone may hold this role.">
              <Select
                size="small"
                mode="multiple"
                allowClear
                /*
                 * ⚠️ [amended C1f §8d] `optionFilterProp` IS THE SEARCH BUG, AND IT IS ONE LINE.
                 *
                 * MEASURED against rc-select: with this unset and no `options` key on the item,
                 * `useFilterOptions` falls back to `includes(option[fieldValue], search)` — i.e.
                 * it filters against the OBJECTID. `RankPicker` and `RankMultiPicker` both pass
                 * `label`; this was the only rank picker in the repository that did not, which
                 * is exactly why §8d reported that "search does not work".
                 */
                optionFilterProp="label"
                /*
                 * ⚠️ [UX1 §6] `loading` AND AN HONEST SETTLED-EMPTY. `RankPicker` and
                 * `RankMultiPicker` both pass `loading={isFetching}`; this was the only rank
                 * picker that did not, so mid-fetch it was visually IDENTICAL to the bug §6 is
                 * about — an empty dropdown with no explanation.
                 *
                 * ⚠️ AND `notFoundContent` NAMES THE STATE OF THE DEPLOYMENT, NOT A COMMAND.
                 * `DEACTIVATED_MESSAGE`'s rule. An operator on a police dashboard cannot act on
                 * "run `seed:ranks`"; they can act on "ask your administrator", and the pnpm half
                 * belongs in CLAUDE.md's command table where the person who CAN act looks.
                 */
                loading={ranksFetching}
                notFoundContent={ranksFetching ? COMMAND_RANK_COPY.loading : COMMAND_RANK_COPY.unavailable}
                maxTagCount="responsive"
                /*
                 * ⚠️ `w-full`, AND `w-72 shrink-0` IS WHAT IT REPLACES. A fixed 288px pinned the
                 * chip area to the same measure on a 2560 screen as on a 1366 one, and — being
                 * `shrink-0` — pushed every pixel of any deficit onto the label beside it. On its
                 * own line there is nothing to take width from and nothing to give it to.
                 */
                className="w-full"
                disabled={disabled}
                placeholder="Any rank"
                /*
                 * ⚠️ [C2b §5c] THE TWO STORED COLUMNS, RE-JOINED FOR ONE CONTROL — and split
                 * again on change. The array order puts groups first, which is the order the
                 * list offers them in.
                 */
                value={[...row.eligibleRankGroupKeys.map(groupValueOf), ...row.eligibleRankIds]}
                /*
                 * ⚠️ [amended C1f §8a] THE FOURTEEN, NOT ALL FORTY-ONE — and `resolveRank` still
                 * reaches every rank, so a role that stored something outside the set before
                 * C1f keeps rendering that rank's NAME. `lib/policyColumns.ts`'s
                 * offer-versus-resolve split, and it is what makes the narrowing safe on live
                 * data rather than a way to lose a stored value.
                 *
                 * ⚠️ [D4.5 §1b] A rank deactivated after this role was written keeps its NAME on
                 * the tag rather than showing an ObjectId — `lib/selectOptions.ts`. It is not
                 * re-offered; the fallback is only ever a value already stored.
                 */
                /*
                 * ⚠️ [C2b §5d] TWO LABELLED SECTIONS OF ONE LIST — antd's own option GROUPS, so
                 * the separator and the headings are structural rather than two fake rows. And
                 * `optionFilterProp="label"` above filters WITHIN both, so search spans them.
                 */
                options={[
                  {
                    label: COMMAND_RANK_COPY.groupSection,
                    options: RANK_GROUPS.map((group) => ({
                      value: groupValueOf(group.key),
                      /* ⚠️ §5d asks for the member count to be VISIBLE, and it is part of the
                       label so search matches it and jsdom can read it. */
                      label: `${group.label} · ${COMMAND_RANK_COPY.members(group.rankCodes.length)}`,
                    })),
                  },
                  {
                    label: COMMAND_RANK_COPY.rankSection,
                    /*
                     * ⚠️ [C2b §5d] THE EMPTY STATE MOVED INTO THE LIST, AND IT HAD TO.
                     *
                     * `notFoundContent` above is [UX1 §6]'s honest settled-empty — "no ranks are
                     * marked command-eligible yet". Adding the GROUPS section made the list never
                     * empty, so that message became unreachable and the operator lost the one
                     * signal that says `seed:ranks` has not been run. It is a disabled row in the
                     * section it is about instead, which is where they are already looking.
                     */
                    options:
                      commandRankOptions.length === 0 && !ranksFetching
                        ? [
                            {
                              value: '__no-command-ranks__',
                              label: COMMAND_RANK_COPY.unavailable,
                              disabled: true,
                            },
                          ]
                        : withChosenOptions(commandRankOptions, row.eligibleRankIds, resolveRank),
                  },
                ]}
                /*
                 * ⚠️ [C2b §5e] A GROUP CHIP IS NOT A RANK CHIP. `data-kind` is what a unit test
                 * reads; the border is what an operator sees. Colour alone would be invisible to
                 * jsdom and to a colour-blind operator alike.
                 */
                tagRender={({ label, value, closable, onClose }) => {
                  const entry = String(value);
                  const isGroup = isGroupValue(entry);
                  /*
                   * ⚠️ THE ACCESSIBLE NAME COMES FROM THE **VALUE**, NEVER FROM `label`.
                   * antd types `label` as a `ReactNode`, so `String(label)` can stringify to
                   * `[object Object]` — `@typescript-eslint/no-base-to-string` refuses it, and it
                   * is right: "Remove [object Object]" is what a screen-reader user would hear.
                   */
                  const name = isGroup
                    ? (RANK_GROUPS.find((group) => group.key === groupKeyOfValue(entry))?.label ?? entry)
                    : (resolveRank(entry)?.name ?? entry);
                  return (
                    <span
                      data-kind={isGroup ? 'group' : 'rank'}
                      className={
                        isGroup
                          ? 'mr-1 inline-flex items-center gap-1 rounded-sm border border-dashed border-border px-1.5 py-0.5 text-xs'
                          : 'mr-1 inline-flex items-center gap-1 rounded-sm bg-secondary px-1.5 py-0.5 text-xs'
                      }
                    >
                      {label}
                      {closable && !disabled ? (
                        <button type="button" aria-label={`Remove ${name}`} onClick={onClose}>
                          ×
                        </button>
                      ) : null}
                    </span>
                  );
                }}
                aria-label={`Ranks eligible for ${row.label || `command role ${String(index + 1)}`}`}
                data-testid={`command-role-ranks-${String(index)}`}
                /* ⚠️ [C2b §5c] SPLIT BACK INTO THE TWO NAMED COLUMNS. The prefix dies here. */
                onChange={(next: string[]) =>
                  update(index, {
                    eligibleRankIds: next.filter((entry) => !isGroupValue(entry)),
                    eligibleRankGroupKeys: next.filter(isGroupValue).map(groupKeyOfValue) as RankGroupKey[],
                  })
                }
              />
            </Tooltip>
          </div>
        ),
    };
  });

  return (
    <div className="flex flex-col gap-2">
      <ReorderableList
        items={items}
        disabled={disabled ?? false}
        ariaLabel="Command roles"
        emptyText="No command roles on this policy. Round-the-clock arrangements often have none."
        data-testid="command-role-list"
        onReorder={(next) => {
          const byId = new Map(rows.map((row, index) => [rowId(row, index), row]));
          onChange?.(next.flatMap((item) => (byId.has(item.id) ? [byId.get(item.id) as CommandRoleRow] : [])));
        }}
        onRemove={(item) => onChange?.(rows.filter((row, index) => rowId(row, index) !== item.id))}
      />

      <div>
        <Button
          size="small"
          disabled={disabled}
          icon={<Plus className="size-3.5" aria-hidden />}
          onClick={() =>
            onChange?.([...rows, { label: '', isRequired: false, eligibleRankIds: [], eligibleRankGroupKeys: [] }])
          }
        >
          Add command role
        </Button>
      </div>
    </div>
  );
};
